import axios from 'axios';
import * as Sentry from '@sentry/react';

/**
 * Setup axios interceptors to capture HTTP errors in Sentry
 * This will send 4xx and 5xx errors to Sentry with full context
 */
export function setupAxiosInterceptors() {
  // Response interceptor to capture errors
  axios.interceptors.response.use(
    (response) => {
      // Successfully received response
      return response;
    },
    (error) => {
      // Handle error response
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { status, statusText, data, config } = error.response;
        
        // Capture 4xx and 5xx errors
        if (status >= 400) {
          Sentry.captureException(error, {
            level: status >= 500 ? 'error' : 'warning',
            tags: {
              http_status: status,
              http_method: config.method?.toUpperCase(),
              request_url: config.url,
            },
            extra: {
              status_code: status,
              status_text: statusText,
              response_data: data,
              request_config: {
                method: config.method,
                url: config.url,
                baseURL: config.baseURL,
                headers: config.headers,
                params: config.params,
              },
            },
            fingerprint: [
              'axios-error',
              String(status),
              config.method?.toUpperCase() || 'UNKNOWN',
              config.url || 'unknown-url',
            ],
          });

          console.error(`[Axios Error ${status}] ${config.method?.toUpperCase()} ${config.url}`, {
            status,
            statusText,
            data,
          });
        }
      } else if (error.request) {
        // The request was made but no response was received
        // This includes network errors, timeouts, etc.
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            error_type: 'network_error',
            request_url: error.config?.url,
          },
          extra: {
            error_message: error.message,
            request_config: error.config,
          },
          fingerprint: [
            'axios-network-error',
            error.config?.url || 'unknown-url',
          ],
        });

        console.error('[Axios Network Error]', error.message, error.config);
      } else {
        // Something happened in setting up the request that triggered an Error
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            error_type: 'request_setup_error',
          },
          extra: {
            error_message: error.message,
          },
        });

        console.error('[Axios Request Setup Error]', error.message);
      }

      // Always reject the promise so the calling code can handle it
      return Promise.reject(error);
    }
  );
}

